import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';

const KEYS = {
  all: ['users'] as const,
  list: (filters?: Record<string, string>) => [...KEYS.all, 'list', filters] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
};

export interface UserListItem {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  role: 'unassigned' | 'provider';
  country?: string;
  provider_count?: number;
  last_login: string | null;
  date_joined: string;
}

export interface UserProviderAssignment {
  id: string;
  provider: string;
  nombre_comercial: string;
  provider_role: 'owner' | 'editor';
}

export interface UserDetail extends UserListItem {
  providers: UserProviderAssignment[];
}

export interface CreateUserData {
  email: string;
  first_name?: string;
  last_name?: string;
  password: string;
  role: 'unassigned' | 'provider';
  is_staff?: boolean;
  is_superuser?: boolean;
}

export interface UpdateUserData {
  role?: 'unassigned' | 'provider';
  is_active?: boolean;
  is_staff?: boolean;
  is_superuser?: boolean;
  first_name?: string;
  last_name?: string;
  country?: string;
}

export function useUserDetail(id: string | null) {
  return useQuery({
    queryKey: KEYS.detail(id ?? ''),
    queryFn: async () => {
      const { data } = await api.get<UserDetail>(`${API_ROUTES.users}${id}/`);
      return data;
    },
    enabled: !!id,
  });
}

export function useUsers(filters?: Record<string, string>) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams(filters);
      const { data } = await api.get<UserListItem[]>(`${API_ROUTES.users}?${params}`);
      return data;
    },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateUserData) => {
      const { data: result } = await api.post(API_ROUTES.users, data);
      return result;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateUserData & { id: string }) => {
      const { data: result } = await api.patch(`${API_ROUTES.users}${id}/`, data);
      return result;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}
